import { showAlertCallout } from "@/shared/alerts";
import { useConfirmPopoverStore } from "@/shared/stores";
import { ERROR_DISPLAY_TYPES } from "@/shared/utils/errors/error.types";
import {
  getTranslationKeyAndDisplayTypes,
  hasErrorDisplayType,
} from "@/shared/utils/errors/get-http-error-type";
import { showHttpErrorAlertCallout } from "@/shared/utils/show-http-error-alert-callout";
import {
  deleteCompanyGroupById,
  getGetCompanyGroupListPaginatedQueryKey,
} from "@/spz-api/company-groups/company-groups";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

type DeleteCompanyGroupMutationVariables = {
  id: string;
  headers?: Record<string, string>;
};
export const useDeleteCompanyGroupDialog = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openPopover, closePopover } = useConfirmPopoverStore();

  const deleteCompanyGroupsByIdMutation = useMutation<
    void,
    unknown,
    DeleteCompanyGroupMutationVariables
  >({
    mutationFn: async (variables) => {
      const { id, headers } = variables;
      await deleteCompanyGroupById(id, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      showAlertCallout({
        type: "success",
        title: t("group_deleted_successfully"),
      });

      queryClient.invalidateQueries({
        queryKey: getGetCompanyGroupListPaginatedQueryKey({}),
      });
      closePopover();
    },
    onError: (error) => {
      const { translationKey, displayTypes } =
        getTranslationKeyAndDisplayTypes(error);
      const message = t(translationKey);
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
        showHttpErrorAlertCallout({
          error: message,
        });
      }
    },
  });

  const handleDeleteClick = ({
    groupId,
    anchorElement,
    updatedAt,
  }: { groupId: string; anchorElement: HTMLElement; updatedAt?: string }) => {
    openPopover({
      description: t("delete_group_confirmation_message"),
      anchorElement,
      onConfirm: () => {
        deleteCompanyGroupsByIdMutation.mutate({
          id: groupId,
          headers: updatedAt ? { "if-match": updatedAt } : undefined,
        });
      },
    });
  };

  return {
    open: handleDeleteClick,
  };
};
